<template>
    <Tree
        ref="tree"
        v-model:expanded-keys="expandedKeys"
        :class="['tree', { 'is-corporate': isCorporate }]"
        selection-mode="single"
        :value="courseResourcesTreeNodes"
        :pt="{
            node: {
                onFocus: showNavTitleTooltipOnFocus,
                onBlur: hideNavTitleTooltipOnBlur,
            },
            nodeContent: {
                class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer tw:flex tw:items-center tw:min-w-0',
            },
            nodeChildren: {
                style: 'padding-inline-start: 1rem !important;',
            },
            nodeToggleButton: {
                class: 'tw:order-last tw:ml-auto!',
            },
            nodeLabel: {
                class: 'tw:flex-1 tw:min-w-0',
            },
        }"
        @node-select="onNodeSelect"
    >
        <template #default="{ node }">
            <!-- Render dynamic component ONLY for leaf nodes (no children) with a type -->
            <component
                :is="getComponentForItem(node.data)"
                v-if="node.data && getComponentForItem(node.data) && !node.children"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0"
                :item="node.data"
                :close-menu="closeMenu"
            />

            <!-- For nodes with children and a type, just render the label (Tree handles expansion) -->
            <span
                v-else-if="node.data && node.children"
                class="tw:flex tw:items-center tw:min-w-0 tw:w-full tw:overflow-hidden"
                :class="{ 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.text ?? node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.text ?? node.label }}</span>
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:min-w-2 tw:min-h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
                    title="Current Week"
                />
            </span>

            <!-- Render router-link if node has a route -->
            <router-link
                v-else-if="node.route"
                :to="node.route"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium! tw:flex tw:items-center tw:w-full tw:min-w-0 tw:overflow-hidden"
                :class="{ 'is-active': node.isActive, 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }"
                @click="closeMenu"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.label }}</span>
                <span
                    v-if="node.isActive"
                    class="tw:inline-block tw:min-w-2 tw:min-h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
                />
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:w-2 tw:h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
                    title="Current Week"
                />
                <e-tag v-if="node.badge != null" type="is-link" size="is-small" rounded class="tw:ml-2 tw:flex-shrink-0">
                    <strong>{{ node.badge }}</strong>
                </e-tag>
            </router-link>

            <!-- For nodes with children and no type/route, just render the label -->
            <span
                v-else-if="node.children"
                class="tw:flex tw:items-center tw:min-w-0 tw:w-full tw:overflow-hidden"
                :class="{ 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }"
            >
                <span
                    v-tooltip.right.hover="navTitleTooltip(node.text ?? node.label)"
                    class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                >{{ node.text ?? node.label }}</span>
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:w-2 tw:h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
                    title="Current Week"
                />
            </span>

            <!-- Fallback: just render the label -->
            <span
                v-else
                v-tooltip.right.hover="navTitleTooltip(node.label)"
                class="nav-title-clamp-2 tw:min-w-0"
            >{{ node.label }}</span>
        </template>
    </Tree>

    <!-- Course Log Modal -->
    <e-modal v-model="openCourseLog">
        <course-log-entries :course-id="courseId" />
    </e-modal>
</template>
<script>
import { pascalCase } from 'change-case';
import capitalize from 'lodash/capitalize.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import { computed } from 'vue';
import { mapState } from 'vuex';

import CourseLogEntries from '@/components/course-log/CourseLogEntries.vue';
import CourseReportsMenu from '@/components/nested-list/CourseReportsMenu.vue';
import NestedList from '@/components/nested-list/NestedList.vue';
import * as NestedListItems from '@/components/nested-list/nested-list-item/Index.js';
import MenuLink from '@/components/nested-list/nested-list-item/MenuLink.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import useSelfPacedCourse from '@/composables/useSelfPacedCourse.js';
import {
    CORPORATE_ENROLLMENT_PROGRESS_MENU_LABEL,
    CORPORATE_GRADEBOOK_MENU_LABEL,
    CORPORATE_META_MENU_LABEL,
    DEFAULT_GRADEBOOK_MENU_LABEL,
    isCorporateHiddenCourseMenuKey,
} from '@/constants/corporateCourseMenu.js';
import { weekRangeInInstitutionTz } from '@/Helpers.js';
import { useLayout } from '@/layouts/composables/layout.js';
import Course from '@/models/Course.js';
import Remediation from '@/models/Remediation.js';
import {
    hideNavTitleTooltipOnBlur,
    navTitleTooltip,
    showNavTitleTooltipOnFocus,
} from '@/utilities/longTitleTooltip.js';
import { nestedListItemKey } from '@/utilities/nestedListItemKey.js';
import { fetchPendingInstructorGradeCount, subscribeRemediationPanelRefresh } from '@/utilities/remediationSidebar.js';
import {
    isCourseDueDatesScheduleRouteName,
    isCourseEditRouteName,
    isCourseMeetingsRouteName,
    isQuizDashboardRouteName,
} from '@/utilities/sidebarMenuRoute.js';

export default {
    name: 'CourseMenu',

    components: {
        CourseReportsMenu,
        CourseLogEntries,
        MenuLink,
        NestedList,
        ...NestedListItems.default,
    },

    emits: ['close-drawer'],

    props: {
        items: {
            default: () => ({}),
            type: Object,
        },

        courseId: {
            type: Number,
            required: true,
        },

        mode: {
            type: String,
            default: 'breadcrumb', // 'breadcrumb' or 'sidebar'
            validator: value => ['breadcrumb', 'sidebar'].includes(value),
        },

        itemToOpen: {
            type: String,
            default: undefined,
        },

        navigateAndClose: {
            type: Function,
            default: undefined,
        },
    },

    setup(props) {
        const course = computed(() => Course.query().with(['groups']).find(props.courseId));
        const { isSelfPacedCourse } = useSelfPacedCourse({ course });
        return { isSelfPacedCourse };
    },

    data: () => ({
        openCourseLog: false,
        isOpen: false,
        currentLabel: '',
        labelSet: false,
        expandedKeys: {},
        pendingGradeRemediationCount: 0,
        remediationCountRequestToken: 0,
    }),

    watch: {
        '$route.name': {
            handler(routeName) {
                if (isCourseEditRouteName(routeName)) {
                    return;
                }

                this.syncSidebarToActiveRoute();
            },
        },

        '$route.params.activityId': {
            handler() {
                if (this.$route.name === 'activity-view') {
                    this.syncSidebarToActiveRoute();
                }
            },
        },

        itemToOpen: {
            handler(newVal) {
                if (!newVal) {
                    return;
                }

                this.applySummaryOpenTarget(newVal);
            },
        },

        courseId: {
            handler() {
                this.loadCourseRemediations();
            },
        },
    },

    mounted() {
        this._unsubscribeRemediationRefresh = subscribeRemediationPanelRefresh(this.handleRemediationPanelRefresh);
        this.setInitialLabel();

        if (this.itemToOpen) {
            this.applySummaryOpenTarget(this.itemToOpen);
        } else {
            this.expandInitialNodes();
        }

        this.expandToActiveNode();
        this.loadCourseRemediations();
    },

    beforeUnmount() {
        this.remediationCountRequestToken++;
        this._unsubscribeRemediationRefresh?.();
    },

    updated() {
        // Try to set label again if it hasn't been set yet
        // This catches cases where nested lists render after mount
        if (!this.labelSet) {
            this.setInitialLabel();
        }
    },

    // eslint-disable-next-line vue/order-in-components
    computed: {
        ...mapState('Auth', { user: state => state.user }),

        course() {
            return Course.query().with(['groups']).find(this.courseId);
        },

        editAccess() {
            if (this.course && this.user.hasRoleWithinCourseGroup(this.course, 'Content Editor')) {
                const course = this.$store.getters['Courses/get'](this.course.id);
                const array = [new Date(course.course_settings.start_date) < new Date(new Date()), course.in_progress];

                return !array.includes(true);
            }

            return true;
        },

        hasRemediation() {
            if (
                !isEmpty(Remediation.query().where('course_id', this.courseId).get()) ||
                this.course.remediation_activities
            ) {
                return true;
            }

            return false;
        },

        canViewCourse() {
            if (!this.user || !this.course) {
                return false;
            }

            return this.user.hasPermissionViaCourseTo(this.course, 'view a course') || this.user.is_admin;
        },

        canViewRemediation() {
            return (
                this.user.hasGroupPermissionWithinCourse(this.courseId, 'view a remediation process') ||
                this.user.hasPermissionTo('view all remediation processes') ||
                this.user.isInstructorOf(this.courseId)
            );
        },

        canShowRemediationBadge() {
            return Boolean(this.user?.hasPermissionTo('view all remediation processes'));
        },

        isCorporate() {
            return isCorporateClient();
        },

        courseResourcesTreeNodes() {
            if (isCorporateClient()) {
                return this.buildCorporateCourseMenuNodes();
            }

            return this.buildStandardCourseMenuNodes();
        },

        treeNodes() {
            const nodes = [];

            return nodes;
        },

        activeActivityId() {
            if (this.$route.name !== 'activity-view') {
                return null;
            }

            const { activityId } = this.$route.params;

            return activityId != null ? String(activityId) : null;
        },

        activeActivitySectionId() {
            if (!this.activeActivityId) {
                return null;
            }

            return this.findSectionIdForActivity(this.activeActivityId);
        },
    },

    methods: {
        get,
        navTitleTooltip,
        showNavTitleTooltipOnFocus,
        hideNavTitleTooltipOnBlur,

        async loadCourseRemediations() {
            const requestToken = ++this.remediationCountRequestToken;
            const { courseId } = this;
            this.pendingGradeRemediationCount = 0;

            if (!courseId || !this.user?.hasPermissionTo('view all remediation processes')) {
                return;
            }

            let count = 0;
            try {
                count = await fetchPendingInstructorGradeCount(courseId);
            } catch {
                // An unavailable count must not leave another course's badge visible.
            }

            if (requestToken === this.remediationCountRequestToken && courseId === this.courseId) {
                this.pendingGradeRemediationCount = count;
            }
        },

        handleRemediationPanelRefresh(event) {
            if (Number(event.detail?.courseId) === Number(this.courseId)) {
                this.loadCourseRemediations();
            }
        },

        syncSidebarToActiveRoute() {
            this.labelSet = false;
            this.$nextTick(() => {
                this.setInitialLabel();
                this.expandToActiveNode();
            });
        },

        resolveWeekSectionHighlight(sectionItem, defaultPresentHighlight = false) {
            if (!sectionItem || sectionItem.type !== 'section') {
                return defaultPresentHighlight;
            }

            if (this.activeActivitySectionId) {
                return String(sectionItem.id) === String(this.activeActivitySectionId);
            }

            return defaultPresentHighlight;
        },

        findSectionIdForActivity(activityId, items = null, sectionId = null) {
            if (!activityId) {
                return null;
            }

            const searchItems = (entries, currentSectionId) => {
                let result = null;

                (entries || []).some(item => {
                    if (item.type === 'activity' && String(item.activity_id) === String(activityId)) {
                        result = currentSectionId;
                        return true;
                    }

                    const nextSectionId =
                        item.type === 'section' && item.section_type === 'week' ? item.id : currentSectionId;

                    if (item.items?.length) {
                        const match = searchItems(item.items, nextSectionId);

                        if (match) {
                            result = match;
                            return true;
                        }
                    }

                    return false;
                });

                return result;
            };

            if (items) {
                return searchItems(items, sectionId);
            }

            const curriculum = this.items?.curriculum;

            if (!curriculum) {
                return null;
            }

            const miscBuckets = ['misc_pre', 'misc_post'];
            const miscMatch = miscBuckets
                .map(bucket => searchItems(curriculum[bucket] || [], null))
                .find(match => match != null);

            if (miscMatch) {
                return miscMatch;
            }

            const weekMatch = ['past', 'present', 'future']
                .flatMap(tense => curriculum.weeks?.[tense] || [])
                .map(week => searchItems(week.items || [], week.id))
                .find(match => match != null);

            if (weekMatch) {
                return weekMatch;
            }

            return null;
        },

        isWeekRelatedExpandKey(key) {
            return (
                key === 'previous-weeks' ||
                key === 'upcoming-weeks' ||
                key.startsWith('previous-weeks-') ||
                key.startsWith('current-weeks-') ||
                key.startsWith('future-weeks-') ||
                key.startsWith('upcoming-weeks-')
            );
        },

        isActiveRouteNode(node, route = this.$route) {
            if (node.route && node.route.name === route.name) {
                return Object.keys(node.route.params || {}).every(
                    key => String(node.route.params[key]) === String(route.params[key])
                );
            }

            if (node.data?.route_name === route.name) {
                const params = node.data.route_params || {};

                return Object.keys(params).every(key => String(params[key]) === String(route.params[key]));
            }

            if (node.data?.activity_id && route.name === 'activity-view') {
                return (
                    String(node.data.activity_id) === String(route.params.activityId) &&
                    String(node.data.course_id) === String(route.params.courseId)
                );
            }

            return false;
        },

        findActiveNodeContext(nodes, ancestors = []) {
            let result = { found: false, ancestorKeys: [] };

            nodes.some(node => {
                if (this.isActiveRouteNode(node)) {
                    result = { found: true, ancestorKeys: ancestors };
                    return true;
                }

                if (node.children?.length) {
                    const childResult = this.findActiveNodeContext(node.children, [...ancestors, node.key]);

                    if (childResult.found) {
                        result = childResult;
                        return true;
                    }
                }

                return false;
            });

            return result;
        },

        scrollActiveActivityIntoView() {
            const treeEl = this.$refs.tree?.$el;

            if (!treeEl) {
                return;
            }

            treeEl.querySelector('a.is-active')?.scrollIntoView({ block: 'nearest' });
        },

        buildStandardCourseMenuNodes() {
            const nodes = [];

            // Stanbridge keeps the "Course Dashboard" entry — the
            // first-activity landing behavior is corporate-only (see the
            // isCorporate guard in Course.vue and buildCorporateCourseMenuNodes).
            nodes.push({
                key: 'course-dashboard',
                label: 'Course Dashboard',
                isActive: this.$route.name === 'course-dashboard',
                route: { name: 'course-dashboard', params: { courseId: this.courseId } },
            });

            if (this.$gate.allows('viewPreview', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'gradebook-preview',
                    label: 'Gradebook Preview',
                    isActive: this.$route.name === 'course-gradebook-preview',
                    route: { name: 'course-gradebook-preview', params: { courseId: this.courseId } },
                });
            }

            if (this.$gate.allows('view', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'gradebook',
                    label: isCorporateClient() ? CORPORATE_GRADEBOOK_MENU_LABEL : DEFAULT_GRADEBOOK_MENU_LABEL,
                    isActive: this.$route.name === 'gradebook-view',
                    route: {
                        name: 'gradebook-view',
                        params: {
                            courseId: this.courseId,
                            gradebookId: get(this.course, 'gradebook.id'),
                        },
                    },
                });
            }

            if (this.$gate.allows('viewStudent', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'grades',
                    label: 'Grades',
                    isActive: this.$route.name === 'student-grade-view',
                    route: {
                        name: 'student-grade-view',
                        params: {
                            courseId: this.courseId,
                            userId: this.user.id,
                        },
                    },
                });
            }

            if (this.canViewCourse && this.items.meta && this.items.meta.length) {
                const metaChildren = this.convertNestedListToTreeNodes(this.items.meta, 'meta');
                if (metaChildren.length > 0) {
                    nodes.push({
                        key: 'meta',
                        label: 'Meta',
                        children: metaChildren,
                    });
                }
            }

            if (this.$gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', this.course])) {
                nodes.push({
                    key: 'enrollments',
                    label: 'Enrollments',
                    isActive: this.$route.name === 'course-enrollments-view',
                    route: { name: 'course-enrollments-view', params: { courseId: this.courseId } },
                });
            }

            // Course Content
            if (
                this.user &&
                this.items.curriculum &&
                this.items.curriculum.misc_pre &&
                this.items.curriculum.misc_pre.length
            ) {
                const courseContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_pre,
                    'course-content'
                );
                if (courseContentChildren.length > 0) {
                    courseContentChildren.forEach(item => {
                        nodes.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            // Previous Weeks
            const previousChildren = [];

            if (this.canViewCourse && this.hasWeeksInBucket('past')) {
                const pastWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.past,
                    'previous-weeks'
                );
                if (pastWeeksChildren.length > 0) {
                    pastWeeksChildren.forEach(item => {
                        previousChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                            isCurrentWeek: this.resolveWeekSectionHighlight(item.data),
                        });
                    });
                }
            }

            if (previousChildren.length > 0) {
                nodes.push({
                    key: 'previous-weeks',
                    label: 'Previous Weeks',
                    children: previousChildren,
                });
            }

            // Current Weeks
            if (this.canViewCourse && this.hasWeeksInBucket('present')) {
                const currentWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.present,
                    'current-weeks'
                );
                currentWeeksChildren.forEach(item => {
                    nodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                        isCurrentWeek: this.resolveWeekSectionHighlight(item.data, true),
                    });
                });
            }

            // Upcoming Weeks
            const upcomingChildren = [];

            if (this.canViewCourse && this.hasWeeksInBucket('future')) {
                const futureWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.future,
                    'future-weeks'
                );
                if (futureWeeksChildren.length > 0) {
                    futureWeeksChildren.forEach(item => {
                        upcomingChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                            isCurrentWeek: this.resolveWeekSectionHighlight(item.data),
                        });
                    });
                }
            }

            if (
                this.canViewCourse &&
                this.items.curriculum &&
                this.items.curriculum.misc_post &&
                this.items.curriculum.misc_post.length
            ) {
                const additionalContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_post,
                    'additional-content'
                );
                if (additionalContentChildren.length > 0) {
                    additionalContentChildren.forEach(item => {
                        upcomingChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            if (upcomingChildren.length > 0) {
                nodes.push({
                    key: 'upcoming-weeks',
                    label: 'Upcoming Weeks',
                    children: upcomingChildren,
                });
            }

            // Course Reports
            if (this.$gate.allows('viewReports', this.course)) {
                const reportChildren = [];

                reportChildren.push({
                    key: 'instructor-engagement',
                    label: 'Instructor Engagement',
                    isActive: this.$route.name === 'discussion-thread-report',
                    route: { name: 'discussion-thread-report' },
                });

                nodes.push({
                    key: 'course-reports',
                    label: 'Course Reports',
                    children: reportChildren,
                });
            }

            if (!this.isSelfPacedCourse && this.$gate.allows('viewAny', ['Attendance', this.course])) {
                nodes.push({
                    key: 'attendance',
                    label: 'Attendance',
                    isActive: this.$route.name === 'course-attendance',
                    route: { name: 'course-attendance', params: { courseId: this.courseId } },
                });
            }

            if (this.$gate.allows('viewAny', ['Meeting', this.courseId]) && this.$hasFeature('MEETINGS')) {
                nodes.push({
                    key: 'meetings',
                    label: 'Meetings',
                    isActive: isCourseMeetingsRouteName(this.$route.name),
                    route: { name: 'course-meetings-view-upcoming' },
                });
            }

            if (this.$gate.allows('viewAny', ['AttemptAuthorization', this.course])) {
                nodes.push({
                    key: 'attempt-authorizations',
                    label: 'Attempt Authorizations',
                    isActive: this.$route.name === 'course-attempt-authorizations-view',
                    route: { name: 'course-attempt-authorizations-view', params: { courseId: this.courseId } },
                });
            }

            if (this.$gate.allows('viewAny', ['QuizDashboard'])) {
                nodes.push({
                    key: 'quiz-dashboard',
                    label: 'Quiz Dashboard',
                    isActive: isQuizDashboardRouteName(this.$route.name),
                    route: { name: 'course-quiz-dashboard', params: { courseId: this.courseId } },
                });
            }

            nodes.push({
                key: 'my-notes',
                label: 'My Notes',
                isActive: this.$route.name === 'course-notes',
                route: { name: 'course-notes' },
            });

            if (this.canViewRemediation) {
                nodes.push({
                    key: 'remediation',
                    label: 'Remediation',
                    isActive: this.$route.name === 'course-remediation',
                    route: { name: 'course-remediation' },
                    badge: this.canShowRemediationBadge ? this.pendingGradeRemediationCount : null,
                });
            }

            if (this.course && this.$gate.allows('viewAny', ['CourseLogEntry', this.course])) {
                nodes.push({
                    key: 'course-log',
                    label: 'Course Log',
                    action: 'openCourseLog',
                    badge: this.course.log_entries_count > 0 ? this.course.log_entries_count : null,
                });
            }

            nodes.push({
                key: 'due-dates',
                label: 'Due Dates and Schedule',
                isActive: isCourseDueDatesScheduleRouteName(this.$route.name),
                route: { name: 'course-activities-view-upcoming', params: { courseId: this.courseId } },
            });

            return nodes;
        },

        buildCorporateCourseMenuNodes() {
            const nodes = [];

            // Admins/instruction admins get a "Course Dashboard" entry at the top
            // so they can reach the course overview (and edit the course). For
            // everyone else it stays omitted: opening a course lands on its first
            // activity, and the dashboard is only a fallback for courses with no
            // navigable activity. See Course.vue@landsOnCourseDashboard.
            if (this.user?.is_admin) {
                nodes.push({
                    key: 'course-dashboard',
                    label: 'Course Dashboard',
                    isActive: this.$route.name === 'course-dashboard',
                    route: { name: 'course-dashboard', params: { courseId: this.courseId } },
                });
            }

            nodes.push(...this.buildCurriculumMenuNodes({ metaLabel: CORPORATE_META_MENU_LABEL }));

            if (this.$gate.allows('viewStudent', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'grades',
                    label: 'Grades',
                    isActive: this.$route.name === 'student-grade-view',
                    route: {
                        name: 'student-grade-view',
                        params: {
                            courseId: this.courseId,
                            userId: this.user.id,
                        },
                    },
                });
            }

            // Consolidated destination: merges progress (gradebook) and
            // enrollments into a single corporate "Enrollment & Progress" page.
            if (this.$gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', this.course])) {
                nodes.push({
                    key: 'enrollment-progress',
                    label: CORPORATE_ENROLLMENT_PROGRESS_MENU_LABEL,
                    isActive: this.$route.name === 'course-progress-view',
                    route: { name: 'course-progress-view', params: { courseId: this.courseId } },
                });
            }

            if (!this.isSelfPacedCourse && this.$gate.allows('viewAny', ['Attendance', this.course])) {
                nodes.push({
                    key: 'attendance',
                    label: 'Attendance',
                    isActive: this.$route.name === 'course-attendance',
                    route: { name: 'course-attendance', params: { courseId: this.courseId } },
                });
            }

            return nodes.filter(node => !isCorporateHiddenCourseMenuKey(node.key));
        },

        buildCurriculumMenuNodes({ metaLabel = 'Meta' } = {}) {
            const curriculumNodes = [];

            if (this.canViewCourse && this.items.meta && this.items.meta.length) {
                const metaChildren = this.convertNestedListToTreeNodes(this.items.meta, 'meta');

                if (metaChildren.length > 0) {
                    curriculumNodes.push({
                        key: 'meta',
                        label: metaLabel,
                        children: metaChildren,
                    });
                }
            }

            if (
                this.user &&
                this.items.curriculum &&
                this.items.curriculum.misc_pre &&
                this.items.curriculum.misc_pre.length
            ) {
                const courseContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_pre,
                    'course-content'
                );

                courseContentChildren.forEach(item => {
                    curriculumNodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                    });
                });
            }

            // Corporate flattens weekly (Time-Based) courses: weeks are listed
            // directly at the top level with no "Previous Weeks" / "Current Weeks"
            // / "Upcoming Weeks" group wrappers. (Self-paced courses have no
            // curriculum.weeks, so this only affects weekly courses.)
            if (this.canViewCourse && this.hasWeeksInBucket('past')) {
                const pastWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.past,
                    'previous-weeks'
                );

                pastWeeksChildren.forEach(item => {
                    curriculumNodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                        isCurrentWeek: this.resolveWeekSectionHighlight(item.data),
                    });
                });
            }

            if (this.canViewCourse && this.hasWeeksInBucket('present')) {
                // Use the raw present weeks so multiple
                // current weeks stay flat instead of being wrapped in a group.
                const currentWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.present,
                    'current-weeks'
                );

                currentWeeksChildren.forEach(item => {
                    curriculumNodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                        isCurrentWeek: this.resolveWeekSectionHighlight(item.data, true),
                    });
                });
            }

            if (this.canViewCourse && this.hasWeeksInBucket('future')) {
                const futureWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.future,
                    'future-weeks'
                );

                futureWeeksChildren.forEach(item => {
                    curriculumNodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                        isCurrentWeek: this.resolveWeekSectionHighlight(item.data),
                    });
                });
            }

            if (
                this.canViewCourse &&
                this.items.curriculum &&
                this.items.curriculum.misc_post &&
                this.items.curriculum.misc_post.length
            ) {
                const additionalContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_post,
                    'additional-content'
                );

                additionalContentChildren.forEach(item => {
                    curriculumNodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                    });
                });
            }

            return curriculumNodes;
        },

        toggleMenu() {
            this.isOpen = !this.isOpen;
        },

        closeMenu() {
            if (this.$isMobile || this.$isTablet) {
                useLayout().toggleSidebarSummary();
                this.$emit('close-drawer');
            }
            this.isOpen = false;
        },

        expandInitialNodes() {
            this.expandWeeksMenu();
        },

        isWeeksSummaryKey(key) {
            if (!key) {
                return false;
            }

            return key === 'previous-weeks' || key === 'upcoming-weeks' || key.startsWith('current-weeks-');
        },

        isCourseContentSummaryKey(key) {
            if (!key) {
                return false;
            }

            return key.startsWith('course-content-') || key.startsWith('additional-content-');
        },

        applySummaryOpenTarget(key) {
            if (this.isWeeksSummaryKey(key)) {
                this.expandWeeksMenu(key);
            } else if (this.isCourseContentSummaryKey(key)) {
                this.expandCourseContentMenu(key);
            } else {
                this.expandedKeys[key] = true;
            }

            this.expandedKeys = { ...this.expandedKeys };
        },

        expandWeeksMenu(targetKey = undefined) {
            const nodes = this.courseResourcesTreeNodes;
            if (!Array.isArray(nodes)) {
                return;
            }

            nodes.forEach(node => {
                if (node?.key?.startsWith('current-weeks')) {
                    this.expandNode(node);
                }
            });

            if (targetKey === 'previous-weeks' || targetKey === 'upcoming-weeks') {
                this.expandedKeys[targetKey] = true;
            } else if (targetKey) {
                this.expandAncestorsOfKey(targetKey);
            }

            this.expandedKeys = { ...this.expandedKeys };
        },

        expandCourseContentMenu(targetKey = undefined) {
            const nodes = this.courseResourcesTreeNodes;
            if (!Array.isArray(nodes)) {
                return;
            }

            nodes.forEach(node => {
                if (node?.key?.startsWith('course-content-')) {
                    this.expandedKeys[node.key] = true;
                }
            });

            if (targetKey?.startsWith('additional-content-')) {
                this.expandedKeys['upcoming-weeks'] = true;
            }

            if (targetKey) {
                this.expandAncestorsOfKey(targetKey);
            }

            this.expandedKeys = { ...this.expandedKeys };
        },

        expandAncestorsOfKey(targetKey) {
            const findAncestors = (nodes, ancestors = []) => {
                if (!Array.isArray(nodes)) {
                    return null;
                }

                return nodes.reduce((found, node) => {
                    if (found) {
                        return found;
                    }

                    if (node.key === targetKey) {
                        return ancestors;
                    }

                    if (node.children) {
                        return findAncestors(node.children, [...ancestors, node.key]);
                    }

                    return null;
                }, null);
            };

            const nodes = this.courseResourcesTreeNodes;
            if (!Array.isArray(nodes)) {
                return;
            }

            const ancestors = findAncestors(nodes);

            if (ancestors) {
                ancestors.forEach(key => {
                    this.expandedKeys[key] = true;
                });
            }

            this.expandedKeys[targetKey] = true;
        },

        expandToActiveNode() {
            const nodes = this.courseResourcesTreeNodes;

            if (!Array.isArray(nodes)) {
                return;
            }

            const { found, ancestorKeys } = this.findActiveNodeContext(nodes);

            if (!found) {
                return;
            }

            const nextExpandedKeys = { ...this.expandedKeys };
            const shouldResetWeekExpansion =
                this.$route.name === 'activity-view' || ancestorKeys.some(key => this.isWeekRelatedExpandKey(key));

            if (shouldResetWeekExpansion) {
                Object.keys(nextExpandedKeys).forEach(key => {
                    if (this.isWeekRelatedExpandKey(key)) {
                        delete nextExpandedKeys[key];
                    }
                });
            }

            ancestorKeys.forEach(key => {
                nextExpandedKeys[key] = true;
            });

            this.expandedKeys = nextExpandedKeys;

            this.$nextTick(() => {
                this.scrollActiveActivityIntoView();
            });
        },

        expandNode(node) {
            if (node.children && node.children.length) {
                this.expandedKeys[node.key] = true;

                node.children.forEach(child => {
                    this.expandNode(child);
                });
            }
        },

        /**
         * Get the component name for a given item type
         * Same logic as the old NestedList component
         */
        getComponentForItem(item) {
            if (!item || !item.type) return null;
            return pascalCase(item.type);
        },

        setInitialLabel() {
            // Wait for DOM to be ready and router links to be rendered
            this.$nextTick(() => {
                const currentPath = this.$route.path;

                // Find all links in the component
                const treeEl = this.$refs.tree?.$el;
                if (!treeEl) return;

                const links = treeEl.querySelectorAll('a');

                // Find the link whose href matches the current path
                const activeLink = Array.from(links).find(link => {
                    const href = link.getAttribute('href');
                    return href && href === currentPath;
                });

                if (activeLink) {
                    // Extract the text content from the active link
                    const labelText = activeLink.textContent?.trim();
                    // Clean up the text (remove extra whitespace, numbers, etc.)
                    const cleanedLabel = labelText?.replace(/\s+/g, ' ').trim();

                    if (cleanedLabel) {
                        this.currentLabel = cleanedLabel;
                        this.labelSet = true;
                    }
                }
            });
        },

        /**
         * @param {'past'|'present'|'future'} tense
         * @return {boolean}
         */
        hasWeeksInBucket(tense) {
            const weeks = get(this.items, `curriculum.weeks.${tense}`);

            return Array.isArray(weeks) && weeks.length > 0;
        },

        onNodeSelect(node) {
            // Handle special actions
            if (node.action === 'openCourseLog') {
                this.openCourseLog = true;
                return;
            }

            // Handle navigation via route
            if (node.route) {
                this.$router.push(node.route);
                this.closeMenu();
                return;
            }

            // Toggle expansion for nodes with children
            if (node.children && node.children.length > 0) {
                if (this.expandedKeys[node.key]) {
                    const newKeys = { ...this.expandedKeys };
                    delete newKeys[node.key];
                    this.expandedKeys = newKeys;
                } else {
                    this.expandedKeys = {
                        ...this.expandedKeys,
                        [node.key]: true,
                    };
                }
            }
        },

        formatItemLabel(item) {
            const text = item.text || item.title || '';

            if (item.type !== 'section' || item.section_type === 'topic') {
                return text || 'Untitled';
            }

            const hasTitle = text && !item.has_fallback_text;
            const optionalTitle = hasTitle ? `: ${text}` : '';
            const type = capitalize(item.section_type);

            let dateRange = '';
            if (item.week_start && item.week_end && !this.$route.name?.includes('temp')) {
                // Use the same timezone source as the editor header (institution timezone from the
                // initial state, no DST hour-nudge) so the side menu and editor agree.
                const { start, end } = weekRangeInInstitutionTz(item.week_start, item.week_end);
                const format = end.isAfter(start, 'year') ? 'MM/DD/YY' : 'MM/DD';
                dateRange = ` (${start.format(format)}-${end.format(format)})`;
            }

            if (item.section_type === 'break') {
                return `${type}${dateRange}${optionalTitle}`;
            }

            if (item.section_type === 'week') {
                const weekNum = item.display_week_number || item.week_number;
                return `${type} ${weekNum}${dateRange}${optionalTitle}`;
            }

            return text || 'Untitled';
        },

        convertNestedListToTreeNodes(items, parentKey = '') {
            if (!items || !Array.isArray(items)) return [];

            return items
                .filter(item => this.hasNestedListPermission(item))
                .map((item, index) => {
                    const key = nestedListItemKey(parentKey, item, index);
                    const node = {
                        key,
                        label: this.formatItemLabel(item),
                        data: item,
                        ...(item.items &&
                            item.items.length > 0 && { children: this.convertNestedListToTreeNodes(item.items, key) }),
                    };

                    // Handle route navigation if item has route info
                    if (item.route_name) {
                        node.route = {
                            name: item.route_name,
                            params: item.route_params || {},
                        };
                        node.isActive = this.$route.name === item.route_name;
                    }

                    return node;
                });
        },

        hasNestedListPermission(item) {
            if (!item.permission) return true;

            const course = Course.query().with(['groups']).find(this.courseId);

            return (
                this.$can(item.permission.action, item.permission.subject) ||
                this.user.hasGroupPermissionWithinCourse(course, item.permission.name)
            );
        },
    },
};
</script>

<style scoped lang="scss">
.p-tree {
    padding: 0;
    padding-block: 0.5rem;
}

:deep(.p-tree-node-content) {
    padding: 0;
    padding-block: 0.25rem;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

:deep(.p-tree-node-toggle-button[data-p-leaf='true']) {
    visibility: hidden;
}

:deep(.p-tree-node-label) {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

:deep(.p-tree-root-children),
:deep(.p-tree-node) {
    min-width: 0;
    max-width: 100%;
}

:deep(a) {
    min-width: 0;
    max-width: 100%;
}

:deep(.p-tree-node-content:has(a.is-active)) {
    background-color: var(--surface-hover);
}

:deep(a.is-active) .nav-title-clamp-2 {
    color: var(--primary-color) !important;
    font-weight: 700 !important;
}

/* Corporate: leaf (activity) rows render a hidden toggle button that PrimeVue still
   reserves at the far right (order-last/ml-auto, 1.75rem + node gap). That phantom
   column pushes the completion check ~2rem inboard of the section chevron above it.
   Collapse the phantom slot so the check's 1.75rem box lands flush in the same
   right-edge column as the section chevron. */
.p-tree.is-corporate :deep(.p-tree-node-leaf > .p-tree-node-content .p-tree-node-toggle-button) {
    display: none;
}
</style>
